import { FC, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import {
  Button,
  Cluster,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Stack,
  TextField,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core/common'

import { useLocalisation } from '@/utils/localisation'

import { TokenFormSchema, TokenFormSchemaType } from './schema'
import { useTokenCreate } from '../api/useTokenCreate'

export type CreateTokenDialogProps = {
  onClose?: () => void
}

export const CreateTokenDialog: FC<CreateTokenDialogProps> = ({ onClose }) => {
  const { translate } = useLocalisation()
  const tokenMutation = useTokenCreate()
  const toaster = useToaster()
  const [createdToken, setCreatedToken] = useState<string | undefined>(undefined)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(TokenFormSchema),
    defaultValues: {
      name: '',
    },
  })
  const onSubmit: SubmitHandler<TokenFormSchemaType> = async (data) => {
    const result = await tokenMutation.mutateAsync({
      data,
    })
    setCreatedToken(result.token)
  }

  return (
    <Dialog
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose?.()
        }
      }}
    >
      {createdToken ? (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate('integrations.tokens.created.title')}</DialogTitle>
          </DialogHeader>
          <Stack gap={16}>
            <Typography variant="body">{translate('integrations.tokens.created.message')}.</Typography>
            <code>{createdToken}</code>
          </Stack>
          <DialogFooter>
            <Button
              onClick={async () => {
                await navigator.clipboard.writeText(createdToken)
                toaster({
                  title: translate('integrations.tokens.created.notifications.clipboard'),
                })
              }}
            >
              <Cluster gap={8} y="center">
                <Icon name="FileCopy" /> {translate('integrations.tokens.created.actions.copy')}
              </Cluster>
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => {
                onClose?.()
              }}
            >
              {translate('integrations.tokens.created.actions.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      ) : (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate('integrations.tokens.create.title')}</DialogTitle>
          </DialogHeader>
          <Stack gap={16}>
            <TextField
              label={translate('integrations.tokens.create.fields.name.label')}
              {...register('name')}
              state={errors.name ? 'negative' : undefined}
            />
          </Stack>
          <DialogFooter>
            <Button onClick={handleSubmit(onSubmit)}>{translate('integrations.tokens.create.actions.create')}</Button>
            <Button
              variant="outline-primary"
              onClick={() => {
                onClose?.()
              }}
            >
              {translate('integrations.tokens.create.actions.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  )
}
